import React, { useEffect, useState } from 'react';
import { Calendar, Play, Plus, Rss, Trash2, Copy, FlaskConical, Check, AlertCircle, Sparkles, X } from 'lucide-react';
import api from '../lib/api';
import HelpCallout from './HelpCallout';

export default function AdminScraper() {
  const [feeds, setFeeds] = useState([]);
  const [runs, setRuns] = useState([]);
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [running, setRunning] = useState(false);
  const [fuzzyGroups, setFuzzyGroups] = useState(null);
  const [scanning, setScanning] = useState(false);

  const [kind, setKind] = useState('jsonld');

  // "Try this venue" tester — dry-run a URL without saving anything
  const [testUrl, setTestUrl] = useState('');
  const [testKind, setTestKind] = useState('auto');
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [actingSid, setActingSid] = useState(null);
  const [audit, setAudit] = useState(null);
  const [startingAudit, setStartingAudit] = useState(false);

  async function startAudit() {
    setStartingAudit(true);
    try {
      await api.post('/admin/scraper/audit');
      pollAudit();
    } catch (e) {
      alert(e.response?.data?.detail || e.message);
    } finally { setStartingAudit(false); }
  }
  function pollAudit() {
    api.get('/admin/scraper/audit/latest').then((r) => {
      setAudit(r.data);
      if (r.data?.status === 'running') setTimeout(pollAudit, 4000);
      else load();
    }).catch(() => {});
  }

  async function approveSuggestion(sid) {
    setActingSid(sid);
    try { await api.post(`/admin/scraper/suggestions/${sid}/approve`); load(); }
    finally { setActingSid(null); }
  }
  async function dismissSuggestion(sid) {
    setActingSid(sid);
    try { await api.post(`/admin/scraper/suggestions/${sid}/dismiss`); load(); }
    finally { setActingSid(null); }
  }

  async function tryVenue() {
    if (!testUrl.trim()) return;
    setTesting(true);
    setTestResult(null);
    try {
      const body = { url: testUrl.trim() };
      if (testKind !== 'auto') body.kind = testKind;
      const r = await api.post('/admin/scraper/test-venue', body);
      setTestResult(r.data);
    } catch (e) {
      setTestResult({ ok: false, error: e.response?.data?.detail || e.message, count: 0, events: [] });
    } finally {
      setTesting(false);
    }
  }

  function addFromTest() {
    setName(testResult?.events?.[0]?.venue || 'New feed');
    setUrl(testUrl);
    setKind(testResult?.kind === 'ics' ? 'ics' : 'jsonld');
    window.scrollTo({ top: document.querySelector('[data-testid="feed-name"]').offsetTop - 100, behavior: 'smooth' });
  }

  function load() {
    api.get('/admin/scraper/feeds').then((r) => setFeeds(r.data.items));
    api.get('/admin/scraper/runs').then((r) => setRuns(r.data.items));
    api.get('/admin/scraper/suggestions').then((r) => setSuggestions(r.data.items)).catch(() => {});
    api.get('/admin/scraper/audit/latest').then((r) => {
      setAudit(r.data);
      if (r.data?.status === 'running') setTimeout(pollAudit, 4000);
    }).catch(() => {});
  }
  useEffect(load, []);

  async function addFeed() {
    if (!name || !url) return;
    await api.post('/admin/scraper/feeds', { name, url, kind, enabled: true });
    setName(''); setUrl(''); load();
  }
  async function toggleFeed(f) {
    await api.patch(`/admin/scraper/feeds/${f.id}`, { enabled: !f.enabled });
    load();
  }
  async function deleteFeed(id) {
    if (!window.confirm('Remove this feed?')) return;
    await api.delete(`/admin/scraper/feeds/${id}`);
    load();
  }
  async function runNow() {
    setRunning(true);
    try { await api.post('/admin/scraper/run'); load(); }
    finally { setRunning(false); }
  }
  async function scanFuzzy() {
    setScanning(true);
    try {
      const r = await api.get('/admin/events/fuzzy-duplicates');
      setFuzzyGroups(r.data.groups || []);
    } finally { setScanning(false); }
  }
  async function deleteEvent(id) {
    if (!window.confirm('Delete this event? This cannot be undone.')) return;
    await api.delete(`/admin/events/${id}`);
    scanFuzzy();
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="label text-heritage mb-2">Auto-scrape</div>
          <h1 className="font-display text-4xl">Event feeds & scraping.</h1>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <button onClick={startAudit} disabled={startingAudit || audit?.status === 'running'} data-testid="scraper-audit-btn" className="border-2 border-heritage text-heritage px-5 py-2.5 rounded-full font-semibold inline-flex items-center gap-2 hover:bg-heritage/5 disabled:opacity-60">
            <Sparkles size={14}/>
            {audit?.status === 'running' ? `Auditing… ${audit.probed || 0}/${audit.total || '?'}` : 'Audit all venues for events'}
          </button>
          <button onClick={runNow} disabled={running} data-testid="scraper-run-now" className="bg-heritage text-base px-6 py-3 rounded-full font-semibold inline-flex items-center gap-2 hover:bg-heritage-deep disabled:opacity-60">
            <Play size={14}/> {running ? 'Running…' : 'Run all scrapes now'}
          </button>
        </div>
      </div>

      <HelpCallout
        title="Keeps your events calendar full automatically."
        description="Every Monday 06:00 UK time, the scraper visits each ICS / iCal feed you've added below (plus tries Eventbrite as a bonus). Anything new goes into the Events page as 'Pending'. NOTHING gets published without your approval — keeping you fully in control of what visitors see."
        appearsAt={[
          { label: '/admin/events Pending queue', where: 'where everything found lands for you to approve' },
          { label: 'Homepage + /events', where: 'only after you Approve a pending event' },
          { label: 'Friday newsletter', where: 'approved events from the scrape feed into the weekend digest' },
        ]}
      />

      <div className="bg-white border border-charcoal/10 rounded-2xl p-6 mb-10">
        <div className="label text-mustard mb-2 flex items-center gap-2"><Calendar size={14}/> Schedule</div>
        <div className="font-display text-2xl mb-2">Automatic run: every Monday at 06:00 UK time</div>
        <p className="text-base/80">
          Add iCal / ICS feed URLs below — most venues, councils and Eventbrite organisation pages publish these.
          Anything found goes into the Events queue as <strong>Pending</strong> for you to approve before it goes live.
        </p>
      </div>

      <div className="bg-white border border-charcoal/10 rounded-2xl p-6 mb-10">
        <div className="label text-heritage mb-4 flex items-center gap-2"><Rss size={14}/> ICS / iCal feed sources</div>
        <div className="space-y-3 mb-6">
          {feeds.length === 0 && (
            <div className="text-muted text-sm italic">No feeds added yet. Examples to try:<br/>
              <code className="text-xs">https://www.visitleicester.info/calendar.ics</code> (if the venue publishes one)<br/>
              <code className="text-xs">https://www.eventbrite.co.uk/o/your-org/events.ics</code> (for an Eventbrite org)
            </div>
          )}
          {feeds.map((f) => (
            <div key={f.id} className="flex items-center gap-3 bg-admin border border-charcoal/10 rounded-xl p-4" data-testid={`feed-${f.id}`}>
              <input type="checkbox" checked={!!f.enabled} onChange={() => toggleFeed(f)} className="shrink-0"/>
              <div className="flex-1 min-w-0">
                <div className="font-semibold flex items-center gap-2">{f.name} <span className="text-[10px] uppercase tracking-wider bg-stone text-charcoal/70 px-1.5 py-0.5 rounded">{f.kind || 'ics'}</span></div>
                <div className="text-xs text-muted truncate">{f.url}</div>
              </div>
              <button onClick={() => deleteFeed(f.id)} className="p-2 text-heritage hover:bg-heritage/5 rounded-lg"><Trash2 size={14}/></button>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Feed name (e.g. Curve Theatre)" data-testid="feed-name" className="md:col-span-3 bg-admin border border-charcoal/15 rounded-xl px-4 py-2.5"/>
          <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://example.com/events.ics or /whats-on/" data-testid="feed-url" className="md:col-span-6 bg-admin border border-charcoal/15 rounded-xl px-4 py-2.5"/>
          <select value={kind} onChange={(e) => setKind(e.target.value)} data-testid="feed-kind" className="md:col-span-1 bg-admin border border-charcoal/15 rounded-xl px-3 py-2.5 text-sm">
            <option value="jsonld">HTML</option>
            <option value="ics">ICS</option>
          </select>
          <button onClick={addFeed} data-testid="feed-add" className="md:col-span-2 bg-forest text-base rounded-xl px-4 py-2.5 font-semibold inline-flex items-center justify-center gap-1"><Plus size={14}/> Add</button>
        </div>
        <p className="text-xs text-muted mt-3">
          <strong>HTML</strong> = a venue&apos;s <code>/whats-on</code> page (we read schema.org Event markup). <strong>ICS</strong> = a direct calendar feed URL. Many venues block server-side requests; if a source returns zero events, the page might be behind Cloudflare or similar — try a different URL on the venue&apos;s site.
        </p>
      </div>

      {suggestions.length > 0 && (
        <div className="bg-white border-2 border-mustard/40 rounded-2xl p-6 mb-10" data-testid="scraper-suggestions-card">
          <div className="label text-mustard mb-2 flex items-center gap-2"><Sparkles size={14}/> Suggested new sources</div>
          <div className="font-display text-2xl mb-2">Venues on your site with events we could scrape.</div>
          <p className="text-base/80 text-sm mb-4">
            When a venue is approved (or audited), we automatically check its website for a scrapeable events page.
            Approve to add it as a feed source — events will then land in the Pending queue on the next scrape.
          </p>
          <div className="space-y-4">
            {suggestions.map((s) => (
              <div key={s.id} className="bg-admin border border-charcoal/10 rounded-xl p-4" data-testid={`suggestion-${s.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="font-semibold">{s.venue_name} <span className="text-[10px] uppercase tracking-wider bg-stone text-charcoal/70 px-1.5 py-0.5 rounded ml-1">{s.trigger}</span></div>
                    <a href={s.events_url} target="_blank" rel="noreferrer" className="text-xs text-heritage underline break-all">{s.events_url}</a>
                    <div className="text-xs text-muted mt-1">{s.dated_events} upcoming event{s.dated_events === 1 ? '' : 's'} found ({s.method})</div>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <button onClick={() => approveSuggestion(s.id)} disabled={actingSid === s.id} data-testid={`suggestion-approve-${s.id}`} className="bg-forest text-base px-4 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-1 disabled:opacity-60"><Check size={13}/> Add as feed</button>
                    <button onClick={() => dismissSuggestion(s.id)} disabled={actingSid === s.id} data-testid={`suggestion-dismiss-${s.id}`} className="border border-charcoal/20 px-4 py-2 rounded-full text-sm inline-flex items-center gap-1 disabled:opacity-60"><X size={13}/> Dismiss</button>
                  </div>
                </div>
                {(s.sample_events || []).length > 0 && (
                  <ul className="mt-3 text-sm space-y-1">
                    {s.sample_events.map((e, i) => (
                      <li key={i} className="flex gap-2 items-baseline">
                        <span className="text-mustard">•</span>
                        <span className="truncate">{e.title}</span>
                        {e.start_date && <span className="text-xs text-muted shrink-0">{String(e.start_date).slice(0, 10)}</span>}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-white border border-charcoal/10 rounded-2xl p-6 mb-10" data-testid="try-venue-card">
        <div className="label text-mustard mb-2 flex items-center gap-2"><FlaskConical size={14}/> Try a venue before adding it</div>
        <div className="font-display text-2xl mb-2">See what we&apos;d find — without saving anything.</div>
        <p className="text-base/80 text-sm mb-4">
          Paste any venue&apos;s <code>/whats-on</code> page or an <code>.ics</code> feed URL and we&apos;ll do a dry-run.
          Use this to check a source actually returns useful events before you commit it as a feed.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
          <input
            value={testUrl}
            onChange={(e) => setTestUrl(e.target.value)}
            placeholder="https://venue.example.com/whats-on/"
            data-testid="try-venue-url"
            className="md:col-span-8 bg-admin border border-charcoal/15 rounded-xl px-4 py-2.5"
          />
          <select
            value={testKind}
            onChange={(e) => setTestKind(e.target.value)}
            data-testid="try-venue-kind"
            className="md:col-span-2 bg-admin border border-charcoal/15 rounded-xl px-3 py-2.5 text-sm"
          >
            <option value="auto">Auto-detect</option>
            <option value="jsonld">HTML</option>
            <option value="ics">ICS</option>
          </select>
          <button
            onClick={tryVenue}
            disabled={testing || !testUrl.trim()}
            data-testid="try-venue-go"
            className="md:col-span-2 bg-charcoal text-base rounded-xl px-4 py-2.5 font-semibold inline-flex items-center justify-center gap-1 disabled:opacity-60"
          >
            <FlaskConical size={14}/> {testing ? 'Testing…' : 'Try it'}
          </button>
        </div>
        {testResult && (
          <div className="mt-5" data-testid="try-venue-result">
            {!testResult.ok && (
              <div className="bg-heritage/10 border border-heritage/40 rounded-xl p-4 text-sm flex items-start gap-2">
                <AlertCircle size={16} className="text-heritage shrink-0 mt-0.5"/>
                <div>
                  <div className="font-semibold text-heritage mb-1">No events found — or the source blocked us.</div>
                  <div className="text-charcoal/70">{testResult.error || 'Try the other format or a different page on the venue\u2019s site.'}</div>
                </div>
              </div>
            )}
            {testResult.ok && testResult.count === 0 && (
              <div className="bg-admin border border-charcoal/10 rounded-xl p-4 text-sm text-muted">
                Reached the page successfully but found 0 future events. The venue might not publish structured event data.
              </div>
            )}
            {testResult.ok && testResult.count > 0 && (
              <>
                <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                  <div className="text-sm">
                    <span className="font-semibold text-forest">Found {testResult.count} event{testResult.count === 1 ? '' : 's'}</span>
                    <span className="text-muted"> · format: {testResult.kind === 'ics' ? 'ICS calendar' : 'HTML / JSON-LD'}</span>
                  </div>
                  <button
                    onClick={addFromTest}
                    data-testid="try-venue-add-as-feed"
                    className="text-xs font-semibold text-heritage hover:underline inline-flex items-center gap-1"
                  >
                    Add this URL as a feed ↓
                  </button>
                </div>
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {testResult.events.map((ev, i) => (
                    <div key={i} className="flex items-start gap-3 bg-admin border border-charcoal/10 rounded-xl p-3 text-sm" data-testid={`try-venue-event-${i}`}>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold truncate">{ev.title}</div>
                        <div className="text-xs text-muted">
                          {ev.start_date ? new Date(ev.start_date).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                          {ev.venue && ` · ${ev.venue}`}
                          {ev.town && ` · ${ev.town}`}
                        </div>
                      </div>
                      {ev.already_in_db ? (
                        <span className="text-[10px] uppercase tracking-wider bg-stone text-charcoal/60 px-2 py-1 rounded inline-flex items-center gap-1 shrink-0">
                          <Check size={11}/> In DB
                        </span>
                      ) : (
                        <span className="text-[10px] uppercase tracking-wider bg-forest/15 text-forest px-2 py-1 rounded shrink-0">New</span>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      <h3 className="font-display text-2xl mb-4">Recent runs</h3>
      <div className="space-y-3">
        {runs.map((r) => (
          <div key={r.id} className="bg-white border border-charcoal/10 rounded-2xl p-5">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
              <div className="label text-charcoal/60">{new Date(r.ran_at).toLocaleString('en-GB')}</div>
              <div className="text-sm">
                <span className="text-forest font-semibold">{r.inserted} added</span>{' · '}
                <span className="text-muted">{r.skipped_dupe} duplicates</span>{' · '}
                {typeof r.skipped_fuzzy === 'number' && (
                  <>
                    <span className="text-muted">{r.skipped_fuzzy} near-duplicates</span>{' · '}
                  </>
                )}
                <span className="text-muted">{r.skipped_invalid} invalid</span>{' · '}
                <span className="text-muted">{r.candidates} candidates</span>
              </div>
            </div>
            {r.sources && (
              <div className="text-xs text-muted flex flex-wrap gap-2">
                {r.sources.map((s, i) => (
                  <span key={i} className="bg-admin px-2 py-1 rounded-md">{s.source}: {s.found}</span>
                ))}
              </div>
            )}
          </div>
        ))}
        {runs.length === 0 && <div className="text-center py-16 text-muted">No runs yet. Click &quot;Run all scrapes now&quot;.</div>}
      </div>

      <div className="mt-12">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <h3 className="font-display text-2xl">Near-duplicate scan</h3>
          <button
            onClick={scanFuzzy}
            disabled={scanning}
            data-testid="scraper-scan-fuzzy"
            className="border border-charcoal/20 text-sm px-4 py-2 rounded-full font-semibold inline-flex items-center gap-2 hover:bg-admin disabled:opacity-60"
          >
            <Copy size={14}/> {scanning ? 'Scanning…' : 'Scan for near-duplicates'}
          </button>
        </div>
        <p className="text-sm text-muted mb-4">Looks for events on the same day with very similar titles that the auto-scraper may have missed in the past.</p>
        {fuzzyGroups && fuzzyGroups.length === 0 && (
          <div className="bg-white border border-charcoal/10 rounded-2xl p-6 text-sm text-muted" data-testid="scraper-fuzzy-empty">
            No near-duplicates found in the upcoming events. Tidy database.
          </div>
        )}
        {fuzzyGroups && fuzzyGroups.length > 0 && (
          <div className="space-y-3" data-testid="scraper-fuzzy-results">
            {fuzzyGroups.map((g, i) => (
              <div key={i} className="bg-white border border-amber-300/50 rounded-2xl p-5">
                <div className="label text-amber-700 mb-3">{g.length} look-alike events on {(g[0].start_date || '').slice(0, 10)}</div>
                <div className="space-y-2">
                  {g.map((ev) => (
                    <div key={ev.id} className="flex items-center justify-between gap-3 text-sm">
                      <div className="min-w-0 flex-1">
                        <div className="font-semibold truncate">{ev.title}</div>
                        <div className="text-xs text-muted truncate">
                          {ev.venue || ev.town || '—'} · status: {ev.status}
                        </div>
                      </div>
                      <button
                        onClick={() => deleteEvent(ev.id)}
                        className="text-red-600 text-xs px-2 py-1 rounded hover:bg-red-50 inline-flex items-center gap-1"
                        data-testid={`scraper-fuzzy-delete-${ev.id}`}
                      >
                        <Trash2 size={12}/> Delete
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
